import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import StarIcon from "@mui/icons-material/Star";

import { PageHeader } from "@/components/PageHeader";
import { useFounderAccess } from "@/hooks/useFounderAccess";
import {
  getRejectionSignalsAnon,
  type RejectionSignalRow,
} from "@/server/rejection-signals";

export const Route = createFileRoute("/app/founder-insights")({
  // Server-side guard: check founder role BEFORE the route renders so
  // non-founders never see any UI flash. Server RPC is still the authoritative
  // backstop for data access.
  beforeLoad: async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    if (!userId) {
      throw redirect({ to: "/login" });
    }
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "founder")
      .maybeSingle();
    if (error || !data) {
      throw redirect({ to: "/app" });
    }
  },
  component: FounderInsightsPage,
});

function FounderInsightsPage() {
  const { isFounder, loading: roleLoading } = useFounderAccess();

  const [rows, setRows] = useState<RejectionSignalRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<"reason" | "family" | "seniority">("reason");

  useEffect(() => {
    if (!isFounder) return;
    let cancelled = false;
    setLoading(true);
    getRejectionSignalsAnon()
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
        setErr(
          res.error
            ? res.error === "FORBIDDEN"
              ? "Access denied — founder role required."
              : res.message ?? "Failed to load signals"
            : null,
        );
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Server functions may throw a Response (e.g. 401 from auth middleware)
        // which serializes as "[object Response]". Detect and show a friendly msg.
        let msg = "Failed to load signals";
        if (e instanceof Response) {
          msg =
            e.status === 401
              ? "Your session expired. Please sign in again."
              : `Request failed (${e.status})`;
        } else if (e instanceof Error) {
          msg = /\[object Response\]/i.test(e.message)
            ? "Your session expired. Please sign in again."
            : e.message;
        }
        setErr(msg);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isFounder]);

  const totals = useMemo(() => {
    if (!rows) return { total: 0, distinctReasons: 0, distinctFamilies: 0, months: 0 };
    const reasons = new Set<string>();
    const families = new Set<string>();
    const months = new Set<string>();
    let total = 0;
    for (const r of rows) {
      reasons.add(r.reason_tag);
      families.add(r.role_family);
      months.add(r.month);
      total += Number(r.occurrences);
    }
    return {
      total,
      distinctReasons: reasons.size,
      distinctFamilies: families.size,
      months: months.size,
    };
  }, [rows]);

  const grouped = useMemo(() => {
    if (!rows) return [];
    const key: keyof RejectionSignalRow =
      groupBy === "reason" ? "reason_tag" : groupBy === "family" ? "role_family" : "seniority";
    const map = new Map<string, number>();
    for (const r of rows) {
      map.set(r[key] as string, (map.get(r[key] as string) ?? 0) + Number(r.occurrences));
    }
    return [...map.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [rows, groupBy]);

  const maxBar = grouped[0]?.count ?? 1;

  if (roleLoading || (!isFounder && loading)) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isFounder) return null;

  return (
    <Box>
      <PageHeader
        title="Founder Insights"
        subtitle="Anonymized cross-user rejection signals — no PII, bucketed by role family, seniority and month."
        actions={
          <Chip
            icon={<StarIcon sx={{ fontSize: 14 }} />}
            label="Founder only"
            color="primary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        }
      />

      {err && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {err}
        </Alert>
      )}

      {/* Summary cards */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        {[
          { label: "Total signals", value: totals.total },
          { label: "Distinct reasons", value: totals.distinctReasons },
          { label: "Role families", value: totals.distinctFamilies },
          { label: "Months covered", value: totals.months },
        ].map((s) => (
          <Card key={s.label} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {s.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                {loading ? "—" : s.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Grouped bar chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2, alignItems: { sm: "center" }, justifyContent: "space-between" }}
          >
            <Typography variant="h6">Top signals</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={groupBy}
              onChange={(_, v) => v && setGroupBy(v)}
            >
              <ToggleButton value="reason">By reason</ToggleButton>
              <ToggleButton value="family">By role family</ToggleButton>
              <ToggleButton value="seniority">By seniority</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : grouped.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No rejection data has been ingested yet. Once users connect or import
              recruiter emails, signals will aggregate here.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {grouped.map((g) => (
                <Box key={g.label}>
                  <Stack direction="row" sx={{ mb: 0.5, justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {g.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {g.count}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(g.count / maxBar) * 100}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Detailed table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Raw signals
          </Typography>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !rows || rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Empty — waiting for ingest volume.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell>Role family</TableCell>
                    <TableCell>Seniority</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Occurrences</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(0, 200).map((r, i) => (
                    <TableRow key={`${r.month}-${r.role_family}-${r.seniority}-${r.reason_tag}-${i}`}>
                      <TableCell>
                        {new Date(r.month).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                        })}
                      </TableCell>
                      <TableCell>{r.role_family}</TableCell>
                      <TableCell>{r.seniority}</TableCell>
                      <TableCell>{r.reason_tag}</TableCell>
                      <TableCell align="right">{r.occurrences}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > 200 && (
                <Box sx={{ p: 1.5, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Showing 200 of {rows.length} rows
                  </Typography>
                </Box>
              )}
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
